import { CommonModule, formatDate } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ShowListItem } from '../../core/models/show.model';
import { ShowsService } from '../../core/services/shows.service';
import { AdminMapperService } from '../../core/services/admin-mapper.service';
import { MATERIAL_IMPORTS } from '../../shared/material';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { TenantConfigService } from '../../core/services/tenant-config.service';
import { AdminMusicalInformationDto } from '../../core/models/admin-api.model';

interface TheaterOption {
  key: string;
  name: string;
  subtitle: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-shows-list',
  standalone: true,
  imports: [CommonModule, ...MATERIAL_IMPORTS],
  templateUrl: './shows-list.component.html',
  styleUrl: './shows-list.component.scss'
})
export class ShowsListComponent implements OnInit {
  private readonly showsService = inject(ShowsService);
  private readonly adminMapper = inject(AdminMapperService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly tenantConfigService = inject(TenantConfigService);

  private readonly adminShowsRangeInDays = environment.adminShowsRangeInDays ?? 14;

  shows: ShowListItem[] = [];
  filteredShows: ShowListItem[] = [];

  isTheaterDropdownOpen = false;
  isDateDropdownOpen = false;

  theaters: TheaterOption[] = [];
  selectedTheater: TheaterOption | null = null;

  selectedDate: Date | null = null;
  minDate: Date | null = null;
  maxDate: Date | null = null;
  isPageLoading = true;

  ngOnInit(): void {
    this.setCalendarRange();
    this.initializeTheaters();
    this.loadInitialShows();
  }

  @HostListener('document:click')
  closeAllDropdowns(): void {
    this.isTheaterDropdownOpen = false;
    this.isDateDropdownOpen = false;
  }

  toggleTheaterDropdown(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.theaters.length) {
      return;
    }

    this.isTheaterDropdownOpen = !this.isTheaterDropdownOpen;
    this.isDateDropdownOpen = false;
  }

  toggleDateDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDateDropdownOpen = !this.isDateDropdownOpen;
    this.isTheaterDropdownOpen = false;
  }

  selectTheater(theater: TheaterOption): void {
    if (theater.disabled) {
      this.isTheaterDropdownOpen = false;
      return;
    }

    this.selectedTheater = theater;
    this.isTheaterDropdownOpen = false;

    if (this.tenantConfigService.hasTenantSelection()) {
      this.tenantConfigService.setCurrentTenant(theater.key);

      /**
       * Clear stale data immediately before loading data for the newly selected tenant.
       * This prevents showing HP data while Wicked fails or returns no data.
       */
      this.clearShowsState();

      if (this.selectedDate) {
        this.loadShowsForSelectedDate(this.selectedDate);
        return;
      }

      this.loadInitialShows();
      return;
    }

    this.applyFilters();
  }

  onDateSelected(date: Date | null): void {
    this.selectedDate = date;
    this.isDateDropdownOpen = false;

    this.clearShowsState();

    if (!date) {
      this.loadInitialShows();
      return;
    }

    this.loadShowsForSelectedDate(date);
  }

  clearDateFilter(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedDate = null;
    this.clearShowsState();
    this.loadInitialShows();
  }

  openShow(show: ShowListItem): void {
    this.router.navigate(['/showsList', show.id]);
  }

  logout(): void {
    this.authService.logout();
  }

  get formattedSelectedDate(): string {
    if (!this.selectedDate) {
      return 'Datum filtern';
    }

    return formatDate(this.selectedDate, 'dd.MM.yyyy', 'de-DE');
  }

  getDateLabel(show: ShowListItem): string {
    return formatDate(show.date, 'EEE, dd.MM.', 'de-DE');
  }

  isSameDate(showDate: string, selectedDate: Date): boolean {
    const date = new Date(showDate);

    return (
      date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate()
    );
  }

  private initializeTheaters(): void {
    if (!this.tenantConfigService.hasTenantSelection()) {
      return;
    }

    this.theaters = this.tenantConfigService.getTenants().map(tenant => ({
      key: tenant.key,
      name: tenant.label,
      subtitle: '',
      disabled: tenant.disabled
    }));

    const currentTenantKey = this.tenantConfigService.getCurrentTenantKey();

    this.selectedTheater =
      this.theaters.find(theater => theater.key === currentTenantKey) ??
      this.theaters.find(theater => !theater.disabled) ??
      null;
  }

  private setCalendarRange(): void {
    const today = new Date();

    this.minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.maxDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + this.adminShowsRangeInDays
    );
  }

  private loadInitialShows(): void {
    this.isPageLoading = true;

    const { startUtc, endUtc } = this.showsService.getInitialAdminRange();

    this.showsService.getShowsPageData(startUtc, endUtc).subscribe({
      next: ({ info, shows }) => {
        this.syncTheaterInfo(info);
        this.shows = shows;
        this.applyFilters();
        this.isPageLoading = false;
      },
      error: error => {
        console.error('Failed to load shows list page.', error);
        this.clearShowsState();
        this.isPageLoading = false;
      }
    });
  }

  private loadShowsForSelectedDate(date: Date): void {
    this.isPageLoading = true;

    const { startUtc, endUtc } = this.showsService.getSingleDayRange(date);

    this.showsService.getShowsPageData(startUtc, endUtc).subscribe({
      next: ({ info, shows }) => {
        this.syncTheaterInfo(info);
        this.shows = shows;
        this.applyFilters();
        this.isPageLoading = false;
      },
      error: error => {
        console.error('Failed to load filtered shows list page.', error);
        this.clearShowsState();
        this.isPageLoading = false;
      }
    });
  }

  private syncTheaterInfo(info: AdminMusicalInformationDto): void {
    if (!this.tenantConfigService.hasTenantSelection()) {
      const option: TheaterOption = {
        key: 'default',
        name: this.adminMapper.toMusicalName(info),
        subtitle: this.adminMapper.toVenueName(info)
      };

      this.theaters = [option];
      this.selectedTheater = option;
      return;
    }

    const currentTenantKey = this.tenantConfigService.getCurrentTenantKey();

    this.theaters = this.theaters.map(theater => {
      if (theater.key !== currentTenantKey) {
        return theater;
      }

      return {
        ...theater,
        subtitle: this.adminMapper.toVenueName(info)
      };
    });

    this.selectedTheater =
      this.theaters.find(theater => theater.key === currentTenantKey) ??
      this.selectedTheater;
  }

  private applyFilters(): void {
    let result = [...this.shows];

    if (this.selectedDate) {
      const selectedDate = this.selectedDate;
      result = result.filter(show => this.isSameDate(show.date, selectedDate));
    }

    this.filteredShows = result;
  }

  private clearShowsState(): void {
    this.shows = [];
    this.filteredShows = [];

    if (this.selectedTheater) {
      this.selectedTheater = {
        ...this.selectedTheater,
        subtitle: ''
      };
    }

    this.theaters = this.theaters.map(theater => ({
      ...theater,
      subtitle: theater.key === this.selectedTheater?.key ? '' : theater.subtitle
    }));
  }
}